import {
  ArrowLeft,
  CreditCard,
  Download,
  Home,
  List,
  LogOut,
  Package,
  ShoppingCart,
  Trophy,
  UserCircle,
  Users,
} from 'lucide-react';
import React from 'react';
import { Nav, Offcanvas } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useInstallPrompt } from '../../hooks/useInstallPrompt';
import api from '../../service/api';
import useGoalsStore from '../../store/goals';
import useUserStore from '../../store/user';
import LanguageSwitcher from '../LanguageSwitcher';

interface SidebarMenuProps {
  show: boolean;
  onHide: () => void;
}

const SidebarMenu: React.FC<SidebarMenuProps> = ({ show, onHide }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useUserStore(state => state.user);
  const clearUser = useUserStore(state => state.clearUser);
  const clearGoals = useGoalsStore(state => state.clearGoals);
  const isManager = user?.isManager || false;
  const { canInstall, installApp } = useInstallPrompt();

  const logout = async () => {
    api.sessionDestroy();
    clearUser();
    clearGoals();
    onHide();
  };

  const userMenuItems = [
    {
      label: t('navigation.profile'),
      href: '/profile',
      icon: <Home size={20} />,
    },
    {
      label: t('navigation.tasks'),
      href: '/tasks',
      icon: <List size={20} />,
    },
    {
      label: t('navigation.goals'),
      href: '/profile',
      icon: <Trophy size={20} />,
    },
    {
      label: t('navigation.shop'),
      href: '/shop',
      icon: <ShoppingCart size={20} />,
    },
    {
      label: t('navigation.inventory'),
      href: '/inventory',
      icon: <Package size={20} />,
    },
  ];

  const managerMenuItems = [
    {
      label: t('manager.navigation.home'),
      href: '/manager/home',
      icon: <Home size={20} />,
    },
    {
      label: t('manager.navigation.tasks'),
      href: '/manager/tasks',
      icon: <List size={20} />,
    },
    {
      label: t('manager.navigation.goals'),
      href: '/manager/goals',
      icon: <Trophy size={20} />,
    },
    {
      label: 'Cards',
      href: '/manager/cards',
      icon: <CreditCard size={20} />,
    },
    {
      label: t('manager.navigation.shop'),
      href: '/manager/shop',
      icon: <ShoppingCart size={20} />,
    },
    {
      label: t('manager.navigation.activations'),
      href: '/manager/activations',
      icon: <Package size={20} />,
    },
    {
      label: t('manager.users.title'),
      href: '/manager/users',
      icon: <Users size={20} />,
    },
    {
      label: t('groups.myGroup'),
      href: '/manager/group',
      icon: <UserCircle size={20} />,
    },
  ];

  const menuItems = isManager ? managerMenuItems : userMenuItems;

  const handleNavigation = (href: string) => {
    navigate(href);
    onHide();
  };

  const handleInstallClick = () => {
    installApp();
    onHide();
  };

  return (
    <Offcanvas show={show} onHide={onHide} placement="start">
      <Offcanvas.Header className="border-bottom">
        <div className="d-flex align-items-center justify-content-between w-100">
          <Offcanvas.Title className="h5 mb-0">
            {t('navigation.menu')}
          </Offcanvas.Title>
          <button
            type="button"
            className="btn btn-link p-0 text-dark"
            onClick={onHide}
            aria-label={t('common.close')}
          >
            <ArrowLeft size={24} />
          </button>
        </div>
      </Offcanvas.Header>

      <Offcanvas.Body className="p-0">
        <Nav className="flex-column">
          {menuItems.map((item, index) => (
            <Nav.Item key={index}>
              <Nav.Link
                as="button"
                className="text-start d-flex align-items-center gap-3 px-4 py-3 border-0 w-100 bg-transparent"
                onClick={() => handleNavigation(item.href)}
                style={{ textDecoration: 'none' }}
              >
                {item.icon}
                <span className="fw-medium">{item.label}</span>
              </Nav.Link>
            </Nav.Item>
          ))}

          {canInstall && (
            <Nav.Item>
              <Nav.Link
                as="button"
                className="text-start d-flex align-items-center gap-3 px-4 py-3 border-0 w-100 bg-transparent"
                onClick={handleInstallClick}
                style={{ textDecoration: 'none' }}
              >
                <Download size={20} />
                <span className="fw-medium">{t('navigation.installApp')}</span>
              </Nav.Link>
            </Nav.Item>
          )}

          {isManager && (
            <Nav.Item className="border-top">
              <Nav.Link
                as="button"
                className="text-start d-flex align-items-center gap-3 px-4 py-3 border-0 w-100 bg-transparent text-danger"
                onClick={logout}
                style={{ textDecoration: 'none' }}
              >
                <LogOut size={20} />
                <span className="fw-medium">{t('common.logout')}</span>
              </Nav.Link>
            </Nav.Item>
          )}
        </Nav>

        <div className="border-top mt-auto p-4">
          <div className="d-flex align-items-center justify-content-between">
            <span className="fw-medium text-muted">{t('common.language')}</span>
            <LanguageSwitcher />
          </div>
        </div>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default SidebarMenu;
